import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import ReactSelect from 'react-select';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import { useJobCardStages } from '../../../context/JobCardStagesContext';
import {
    Typography,
    Chip,
    Button,
    Input
} from '@material-tailwind/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faPlus,
    faEye,
    faEdit,
    faFilter,
    faSyncAlt,
    faExclamationTriangle,
    faFileExport,
    faLockOpen,
    faCircleInfo,
    faWrench,
    faChartBar,
    faEllipsisV
} from '@fortawesome/free-solid-svg-icons';
import { rootUrl } from '../../../config/config';
import Alerty from '../../common/Alerty';
import ConfirmModal from '../../common/ConfirmModal';

const JobCardsList = ({ onView, onEdit, onCreate, plateNo = null }) => {
    const JOB_CARD_LIST_STATE_KEY = 'job_cards_list_state';
    const DEFAULT_JOB_CARD_STAGES = [
        { code: 'OPEN', label: 'Open', name: 'Open', color: '#2563eb' },
        { code: 'IN_PROGRESS', label: 'In Progress', name: 'In Progress', color: '#f59e0b' },
        { code: 'COMPLETED', label: 'Completed', name: 'Completed', color: '#16a34a' },
        { code: 'INVOICED', label: 'Invoiced', name: 'Invoiced', color: '#9333ea' },
        { code: 'CLOSED', label: 'Closed', name: 'Closed', color: '#6b7280' },
        { code: 'CANCELLED', label: 'Cancelled', name: 'Cancelled', color: '#dc2626' }
    ];

    const DEFAULT_FILTERS = {
        status: '',
        service_location: '',
        date_from: '',
        date_to: '',
        owner_code: '',
        invoiced: '',
        plate_no: ''
    };

    const hasSavedFilters = (maybeFilters = {}) => Object.values({
        ...DEFAULT_FILTERS,
        ...maybeFilters
    }).some(value => value !== '' && value !== null && typeof value !== 'undefined');

    const readStoredListState = () => {
        if (typeof window === 'undefined') {
            return null;
        }

        try {
            const cached = localStorage.getItem(JOB_CARD_LIST_STATE_KEY);
            if (!cached) {
                return null;
            }

            const parsed = JSON.parse(cached);
            if (parsed && typeof parsed === 'object' && parsed.filters) {
                return parsed;
            }

            // Backward compatibility with the old filters-only cache shape.
            if (parsed && typeof parsed === 'object') {
                return {
                    filters: parsed,
                    searchTerm: '',
                    showAdvancedFilters: false,
                    activePeriod: null
                };
            }
        } catch (e) {
            console.error('Error parsing cached job card list state:', e);
        }

        return null;
    };

    const { token } = useAuth();
    const authToken = token || localStorage.getItem('authToken') || 'default-token';
    const { stages, loading: stagesLoading, error: stagesError } = useJobCardStages();
    const navigate = useNavigate();
    const cachedListState = readStoredListState();

    const [jobCards, setJobCards] = useState([]);
    const [loading, setLoading] = useState(false);
    const [filters, setFilters] = useState(() => {
        return {
            ...DEFAULT_FILTERS,
            ...(cachedListState?.filters || {})
        };
    });
    const [searchTerm, setSearchTerm] = useState(() => cachedListState?.searchTerm || '');
    const [showAdvancedFilters, setShowAdvancedFilters] = useState(() => (
        cachedListState?.showAdvancedFilters || hasSavedFilters(cachedListState?.filters) || false
    ));
    const [activePeriod, setActivePeriod] = useState(() => cachedListState?.activePeriod || null);

    useEffect(() => {
        if (typeof window === 'undefined') {
            return;
        }

        try {
            localStorage.setItem(JOB_CARD_LIST_STATE_KEY, JSON.stringify({
                filters,
                searchTerm,
                showAdvancedFilters,
                activePeriod
            }));
        } catch (e) {
            console.error('Error saving job card list state:', e);
        }
    }, [filters, searchTerm, showAdvancedFilters, activePeriod]);

    const [owners, setOwners] = useState([]);
    const [exportFormat, setExportFormat] = useState('csv');
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(20);
    const [totalCount, setTotalCount] = useState(0);
    const [selectedCardIds, setSelectedCardIds] = useState(() => new Set());
    const [selectedStatus, setSelectedStatus] = useState('');
    const [bulkStatus, setBulkStatus] = useState('');
    const [showBulkConfirm, setShowBulkConfirm] = useState(false);
    const [bulkLoading, setBulkLoading] = useState(false);
    const [alertOpen, setAlertOpen] = useState(false);
    const [alertMessage, setAlertMessage] = useState('');
    const [alertColor, setAlertColor] = useState('red');
    const [diagnosisTooltip, setDiagnosisTooltip] = useState({ visible: false, x: 0, y: 0, text: '' });

    const applyPeriod = (value) => {
        setActivePeriod(value);
        if (!value) {
            setFilters(f => ({ ...f, date_from: '', date_to: '' }));
            return;
        }
        const today = new Date();
        const fmt = (d) => d.toISOString().split('T')[0];
        const monday = (d) => { const m = new Date(d); m.setDate(d.getDate() - ((d.getDay() + 6) % 7)); return m; };
        let from, to;
        if (value === 'this_week') {
            from = monday(today);
            to = new Date(from); to.setDate(from.getDate() + 6);
        } else if (value === 'last_week') {
            const prevMon = monday(today); prevMon.setDate(prevMon.getDate() - 7);
            from = prevMon;
            to = new Date(from); to.setDate(from.getDate() + 6);
        } else if (value === 'this_month') {
            from = new Date(today.getFullYear(), today.getMonth(), 1);
            to = new Date(today.getFullYear(), today.getMonth() + 1, 0);
        } else if (value === 'last_month') {
            from = new Date(today.getFullYear(), today.getMonth() - 1, 1);
            to = new Date(today.getFullYear(), today.getMonth(), 0);
        }
        setFilters(f => ({ ...f, date_from: fmt(from), date_to: fmt(to) }));
    };
    const [openMenu, setOpenMenu] = useState(null); // { id, x, y, card }
    const { user } = useAuth();

    useEffect(() => {
        setSelectedCardIds(new Set());
        setSelectedStatus('');
        setBulkStatus('');
    }, [page, pageSize, searchTerm, plateNo, filters.status, filters.service_location, filters.date_from, filters.date_to, filters.owner_code, filters.invoiced]);

    const baseStages = Array.isArray(stages) ? stages : [];
    const mergedStages = [
        ...baseStages,
        ...DEFAULT_JOB_CARD_STAGES.filter(defaultStage => !baseStages.some(stage => stage.code === defaultStage.code))
    ];

    useEffect(() => {
        if (!openMenu) return;
        const handleEscape = (e) => { if (e.key === 'Escape') setOpenMenu(null); };
        document.addEventListener('keydown', handleEscape);
        return () => document.removeEventListener('keydown', handleEscape);
    }, [openMenu]);

    const openActionMenu = (e, id, card) => {
        e.stopPropagation();
        if (openMenu?.id === id) { setOpenMenu(null); return; }
        const r = e.currentTarget.getBoundingClientRect();
        setOpenMenu({ id, card, x: r.right, y: r.bottom + 4 });
    };
    const showAlert = (message, color = 'red') => {
        setAlertMessage(message);
        setAlertColor(color);
        setAlertOpen(true);
    };

    const fetchJobCards = useCallback(async () => {
        setLoading(true);
        try {
            const params = {
                ...filters,
                plate_no: plateNo || filters.plate_no || undefined,
                page: page,
                limit: pageSize,
                search: searchTerm || undefined
            };

            // Remove empty filters
            Object.keys(params).forEach(key => {
                if (params[key] === '' || params[key] === undefined) {
                    delete params[key];
                }
            });

            const response = await axios.get(`${rootUrl}/api/job-cards`, {
                headers: { 'Authorization': `Bearer ${authToken}` },
                params
            });

            // Ensure jobCards is always an array
            let data = [];
            if (response.data && response.data.success && Array.isArray(response.data.data)) {
                data = response.data.data;
            } else if (Array.isArray(response.data)) {
                data = response.data;
            } else if (response.data && Array.isArray(response.data.data)) {
                data = response.data.data;
            }

            // If backend didn't provide total/meta and didn't apply search, apply client-side search as fallback
            let filtered = data;
            const backendHasMeta = !!(response.data && (response.data.meta || response.data.total || response.data.total_count || response.headers['x-total-count']));

            if (!backendHasMeta && searchTerm && filtered.length > 0) {
                filtered = filtered.filter(card =>
                    card.job_card_no?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                    card.plate_no?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                    card.diagnosis?.toLowerCase().includes(searchTerm.toLowerCase())
                );
            }

            setJobCards(filtered);

            // Determine total count from common backend shapes or fallback to filtered length
            const totalFromMeta = response.data?.meta?.total || response.data?.total_count || response.data?.total || parseInt(response.headers['x-total-count']);
            setTotalCount(Number.isFinite(Number(totalFromMeta)) ? Number(totalFromMeta) : filtered.length);
        } catch (error) {
            console.error('Error fetching job cards:', error);
            setJobCards([]); // Ensure jobCards is always an array even on error
            setTotalCount(0);
        } finally {
            setLoading(false);
        }
    }, [authToken, filters, plateNo, searchTerm, page, pageSize]);

    // Reset to first page when filters, searchTerm or plate filter change
    useEffect(() => {
        setPage(1);
    }, [filters, searchTerm, plateNo]);

    // Fetch owners for owner filter
    useEffect(() => {
        const fetchOwners = async () => {
            try {
                const res = await axios.get(`${rootUrl}/owners`, { headers: { 'Authorization': `Bearer ${authToken}` } });
                if (res.data && res.data.success && Array.isArray(res.data.data)) {
                    setOwners(res.data.data.map(o => ({ value: o.code, label: `${o.code} - ${o.name}` })));
                } else if (Array.isArray(res.data)) {
                    setOwners(res.data.map(o => ({ value: o.code, label: `${o.code} - ${o.name}` })));
                }
            } catch (err) {
                // ignore owner load errors
                console.error('Error loading owners for filter', err);
            }
        };

        fetchOwners();
    }, [authToken]);

    const handleExport = async () => {
        try {
            const params = {
                ...filters,
                plate_no: plateNo || filters.plate_no || undefined,
                search: searchTerm || undefined
            };

            // Remove empty filters
            Object.keys(params).forEach(key => {
                if (params[key] === '' || params[key] === undefined) {
                    delete params[key];
                }
            });

            const response = await axios.get(`${rootUrl}/api/job-cards/export`, {
                headers: { 'Authorization': `Bearer ${authToken}` },
                params,
                responseType: 'blob'
            });

            const blob = response.data instanceof Blob
                ? response.data
                : new Blob([response.data], { type: 'text/csv;charset=utf-8;' });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            const contentDisposition = response.headers?.['content-disposition'] || '';
            const match = contentDisposition.match(/filename="?([^"]+)"?/i);
            const fileName = match?.[1] || `job-cards_${new Date().toISOString().split('T')[0]}.csv`;
            link.setAttribute('download', fileName);
            document.body.appendChild(link);
            link.click();
            link.parentNode.removeChild(link);
            window.setTimeout(() => window.URL.revokeObjectURL(url), 1000);
        } catch (error) {
            console.error('Error exporting job cards:', error);
            let message = 'Failed to export CSV';
            const errorData = error.response?.data;

            if (errorData instanceof Blob) {
                try {
                    const text = await errorData.text();
                    const parsed = JSON.parse(text);
                    message = parsed.message || message;
                } catch (blobError) {
                    message = 'Failed to export CSV';
                }
            } else if (typeof errorData === 'object' && errorData?.message) {
                message = errorData.message;
            } else if (error.response?.data?.message) {
                message = error.response.data.message;
            } else if (error.message) {
                message = error.message;
            }

            showAlert(message, 'red');
        }
    };

    // Wrapper to respect selected export format
    const handleExportClick = async () => {
        if (exportFormat !== 'csv') {
            showAlert('PDF export is not yet supported. Choose CSV.', 'red');
            return;
        }

        await handleExport();
    };

    // Refresh when component mounts or when filters change
    useEffect(() => {
        fetchJobCards();
    }, [fetchJobCards]);



    const getServiceLocationColor = (location) => {
        return location === 'in_house' ? 'green' : 'orange';
    };

    const formatDate = (date) => {
        if (!date) return '-';
        return new Date(date).toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric'
        });
    };

    const formatCurrency = (amount) => {
        if (!amount) return '0.00';
        return new Intl.NumberFormat('en-KE', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        }).format(amount);
    };

    // Calculate statistics
    const stats = {
        total: totalCount || jobCards.length,
        open: jobCards.filter(c => c.status === 'open').length,
        inProgress: jobCards.filter(c => c.status === 'in_progress').length,
        totalCost: jobCards.reduce((sum, c) => sum + (parseFloat(c.total_cost) || 0), 0)
    };

    // Pagination derived values
    const pages = Math.max(1, Math.ceil((totalCount || jobCards.length) / pageSize));
    const startItem = (page - 1) * pageSize + 1;
    const endItem = Math.min((page - 1) * pageSize + jobCards.length, totalCount || jobCards.length);

    const statusOptions = mergedStages.map(stage => ({
        value: stage.code,
        label: stage.name || stage.label || stage.code,
        color: stage.color
    }));

    const statusByCode = baseStages.reduce((acc, stage) => {
        acc[stage.code] = stage;
        return acc;
    }, {});
    mergedStages.forEach(stage => {
        if (!statusByCode[stage.code]) {
            statusByCode[stage.code] = stage;
        }
    });

    const getStatusMeta = (status) => {
        const stage = statusByCode[status];
        return {
            label: stage?.name || status || 'Unknown',
            color: stage?.color || '#64748b'
        };
    };

    const getStatusStyle = (status) => {
        const meta = getStatusMeta(status);
        return {
            backgroundColor: `${meta.color}18`,
            color: meta.color,
            border: `1px solid ${meta.color}40`
        };
    };

    const toggleCardSelection = (card) => {
        const isSelected = selectedCardIds.has(card.id);

        if (!isSelected && selectedStatus && selectedStatus !== card.status) {
            showAlert('Select rows with the same status before applying a bulk change.', 'red');
            return;
        }

        setSelectedCardIds(prev => {
            const next = new Set(prev);
            if (next.has(card.id)) {
                next.delete(card.id);
            } else {
                next.add(card.id);
            }
            return next;
        });

        if (!isSelected && !selectedStatus) {
            setSelectedStatus(card.status);
        }

        if (isSelected && selectedCardIds.size === 1) {
            setSelectedStatus('');
        }
    };

    const visibleStatuses = Array.from(new Set(jobCards.map(card => card.status)));
    const isAllVisibleSelected = visibleStatuses.length === 1 && jobCards.length > 0 && jobCards.every(card => selectedCardIds.has(card.id));

    const toggleSelectAllVisible = () => {
        if (jobCards.length === 0) return;

        if (visibleStatuses.length > 1) {
            showAlert('Select rows with the same status before using the bulk action.', 'red');
            return;
        }

        const allSelected = jobCards.every(card => selectedCardIds.has(card.id));
        if (allSelected) {
            setSelectedCardIds(new Set());
            setSelectedStatus('');
            return;
        }

        setSelectedCardIds(new Set(jobCards.map(card => card.id)));
        setSelectedStatus(jobCards[0].status);
    };

    const clearBulkSelection = () => {
        setSelectedCardIds(new Set());
        setSelectedStatus('');
        setBulkStatus('');
    };

    const handleOpenBulkConfirm = () => {
        if (selectedCardIds.size === 0) {
            showAlert('Select at least one job card first.', 'red');
            return;
        }
        if (!bulkStatus) {
            showAlert('Choose a target status first.', 'red');
            return;
        }
        setShowBulkConfirm(true);
    };

    const handleBulkStatusUpdate = async () => {
        if (!bulkStatus || selectedCardIds.size === 0) return;

        const targetMeta = getStatusMeta(bulkStatus);
        const selectedCards = jobCards.filter(card => selectedCardIds.has(card.id));
        const cardsToUpdate = selectedCards.filter(card => card.status !== bulkStatus);

        if (cardsToUpdate.length === 0) {
            showAlert(`All selected job cards are already ${targetMeta.label}.`, 'green');
            setShowBulkConfirm(false);
            clearBulkSelection();
            return;
        }

        setBulkLoading(true);
        setShowBulkConfirm(false);

        const results = [];

        try {
            for (const card of cardsToUpdate) {
                try {
                    const response = await axios.put(
                        `${rootUrl}/api/job-cards/${card.id}/status`,
                        {
                            status: bulkStatus,
                            updated_by: user?.username || 'Unknown',
                            notes: `Bulk status update to ${targetMeta.label}`
                        },
                        { headers: { Authorization: `Bearer ${authToken}` } }
                    );

                    if (response.data?.success) {
                        results.push({ id: card.id, success: true });
                    } else {
                        results.push({
                            id: card.id,
                            success: false,
                            message: response.data?.message || 'Failed to update status'
                        });
                    }
                } catch (error) {
                    results.push({
                        id: card.id,
                        success: false,
                        message: error.response?.data?.message || error.message || 'Failed to update status'
                    });
                }
            }

            const successCount = results.filter(result => result.success).length;
            const failureCount = results.length - successCount;
            const firstFailure = results.find(result => !result.success);

            if (failureCount === 0) {
                showAlert(`Updated ${successCount} job card(s) to ${targetMeta.label}.`, 'green');
            } else {
                showAlert(
                    `Updated ${successCount} job card(s) to ${targetMeta.label}. ${failureCount} update(s) failed${firstFailure?.message ? `: ${firstFailure.message}` : ''}.`,
                    'red'
                );
            }

            clearBulkSelection();
            fetchJobCards();
        } finally {
            setBulkLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 pb-8">
            {/* Fixed diagnosis tooltip — escapes all overflow containers */}
            {diagnosisTooltip.visible && (
                <div
                    style={{
                        position: 'fixed',
                        left: diagnosisTooltip.x,
                        top: diagnosisTooltip.y,
                        zIndex: 9999,
                        width: '256px',
                        backgroundColor: '#1e293b',
                        color: '#f1f5f9',
                        fontSize: '0.75rem',
                        lineHeight: '1.4',
                        borderRadius: '8px',
                        padding: '10px 12px',
                        boxShadow: '0 10px 25px rgba(0,0,0,0.3)',
                        pointerEvents: 'none',
                    }}
                >
                    <div style={{
                        position: 'absolute',
                        top: '-9px',
                        left: '14px',
                        width: 0,
                        height: 0,
                        borderLeft: '5px solid transparent',
                        borderRight: '5px solid transparent',
                        borderBottom: '9px solid #1e293b',
                    }} />
                    <p style={{ fontWeight: 600, color: '#94a3b8', marginBottom: '4px' }}>Diagnosis</p>
                    <p>{diagnosisTooltip.text}</p>
                </div>
            )}

            {/* Fixed action dropdown — escapes overflow-x-auto clipping */}
            {openMenu && (
                <>
                    <div className="fixed inset-0 z-[9998]" onClick={() => setOpenMenu(null)} />
                    <div
                        style={{
                            position: 'fixed',
                            right: 'auto',
                            left: openMenu.x - 150,
                            top: openMenu.y,
                            zIndex: 9999,
                        }}
                        className="min-w-[150px] overflow-hidden rounded-sm border border-slate-200 bg-white py-1 shadow-lg"
                    >
                        {onView && (
                            <button
                                type="button"
                                className="flex w-full items-center gap-2 px-3 py-2 text-xs text-slate-700 hover:bg-slate-100"
                                onClick={() => { setOpenMenu(null); onView(openMenu.card); }}
                            >
                                <FontAwesomeIcon icon={faEye} className="w-3" />
                                View
                            </button>
                        )}
                        {onEdit && (
                            <button
                                type="button"
                                className="flex w-full items-center gap-2 px-3 py-2 text-xs text-slate-700 hover:bg-slate-100"
                                onClick={() => { setOpenMenu(null); onEdit(openMenu.card); }}
                            >
                                <FontAwesomeIcon icon={faEdit} className="w-3" />
                                Edit
                            </button>
                        )}
                    </div>
                </>
            )}

            {/* Alert Component */}
            <ConfirmModal
                isOpen={showBulkConfirm}
                title="Apply status to selected job cards"
                description={
                    bulkStatus
                        ? `This will apply ${getStatusMeta(bulkStatus).label} to ${selectedCardIds.size} selected job card${selectedCardIds.size !== 1 ? 's' : ''} on the current page.`
                        : 'Select a status before continuing.'
                }
                onCancel={() => setShowBulkConfirm(false)}
                onConfirm={handleBulkStatusUpdate}
                confirmText="Apply Status"
                cancelText="Cancel"
                loading={bulkLoading}
                variant="primary"
            />
            <div className="fixed top-4 right-4 z-50 max-w-md">
                <Alerty
                    open={alertOpen}
                    setOpen={setAlertOpen}
                    color={alertColor}
                    message={alertMessage}
                    variant="filled"
                />
            </div>



            {/* Filters Section with compact layout + advanced toggle */}
            <div className="px-4 sm:px-6 lg:px-8 py-2">
                <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-3 sm:p-4">
                    <div className="flex items-center justify-between gap-3 mb-3">
                        <div className="flex items-center gap-3 min-w-0 flex-1">
                            <Typography variant="small" className="text-slate-700 font-semibold whitespace-nowrap hidden sm:block">Search</Typography>
                            <div className="hidden sm:flex items-center gap-2 flex-1">
                                <div className="flex-1 min-w-0">
                                    <Input
                                        placeholder="Search job card, plate or diagnosis"
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                        className="text-sm"
                                    />
                                </div>
                                <div className="w-44 flex-shrink-0">
                                    <ReactSelect
                                        placeholder="All statuses"
                                        classNamePrefix="react-select"
                                        isClearable
                                        options={[{ value: '', label: 'All statuses' }, ...stages.map(s => ({ value: s.code, label: s.name }))]}
                                        value={filters.status ? { value: filters.status, label: stages.find(s => s.code === filters.status)?.name } : null}
                                        onChange={(opt) => setFilters({ ...filters, status: opt?.value || '' })}
                                        styles={{ control: (base) => ({ ...base, minHeight: '38px', fontSize: '0.875rem' }) }}
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="flex items-center gap-2">
                            <button
                                onClick={fetchJobCards}
                                className="flex items-center gap-2 px-3 py-1 bg-slate-600 hover:bg-slate-700 text-white text-sm font-medium rounded-md transition-colors"
                                title="Refresh"
                            >
                                <FontAwesomeIcon icon={faSyncAlt} className="text-xs" />
                            </button>
                            <select
                                value={exportFormat}
                                onChange={(e) => setExportFormat(e.target.value)}
                                className="text-sm px-2 py-1 rounded-md border bg-white"
                                aria-label="Export format"
                            >
                                <option value="csv">CSV</option>
                                <option value="pdf">PDF</option>
                            </select>
                            <button
                                onClick={handleExportClick}
                                className="hidden sm:inline-flex items-center gap-2 px-3 py-1 bg-gray-700 hover:bg-gray-800 text-white text-sm font-medium rounded-md transition-colors"
                            >
                                <FontAwesomeIcon icon={faFileExport} className="text-xs" />
                                Export
                            </button>
                            {onCreate && (
                                <button
                                    onClick={onCreate}
                                    className="flex items-center gap-2 px-3 py-1 bg-[#FF5733] hover:bg-[#e64520] text-white text-sm font-semibold rounded-sm shadow-none transition-colors"
                                >
                                    <FontAwesomeIcon icon={faPlus} className="text-sm" />
                                    <span className="ml-1">New Job Card</span>
                                </button>
                            )}
                            <button
                                onClick={() => setShowAdvancedFilters(v => !v)}
                                className="flex items-center gap-2 px-3 py-1 bg-slate-100 hover:bg-slate-200 text-slate-700 text-sm rounded-md"
                                aria-expanded={showAdvancedFilters}
                            >
                                <FontAwesomeIcon icon={faFilter} className="text-xs" />
                                <span className="hidden sm:inline">More filters</span>
                            </button>
                        </div>
                    </div>

                    {/* Compact row for small screens - shows primary controls */}
                    <div className="flex flex-col sm:hidden gap-2 mb-2">
                        <Input
                            placeholder="Search job card, plate or diagnosis"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="text-sm"
                        />
                        <div className="flex gap-2 flex-wrap">
                            <div className="flex-1 min-w-[100px]">
                                <ReactSelect
                                    placeholder="Status"
                                    classNamePrefix="react-select"
                                    isClearable
                                    options={[{ value: '', label: 'Any' }, ...stages.map(s => ({ value: s.code, label: s.name }))]}
                                    value={filters.status ? { value: filters.status, label: stages.find(s => s.code === filters.status)?.name } : null}
                                    onChange={(opt) => setFilters({ ...filters, status: opt?.value || '' })}
                                />
                            </div>
                            <div className="flex-1 min-w-[100px]">
                                <ReactSelect
                                    placeholder="Owner"
                                    classNamePrefix="react-select"
                                    isClearable
                                    options={[{ value: '', label: 'Any' }, ...owners]}
                                    value={filters.owner_code ? (owners.find(o => o.value === filters.owner_code) || null) : null}
                                    onChange={(opt) => setFilters({ ...filters, owner_code: opt?.value || '' })}
                                />
                            </div>
                            <div className="w-28 flex-shrink-0">
                                <Input
                                    placeholder="Plate"
                                    value={filters.plate_no}
                                    onChange={(e) => setFilters({ ...filters, plate_no: e.target.value.trim() })}
                                    className="text-sm"
                                    disabled={!!plateNo}
                                />
                            </div>
                        </div>
                    </div>

                    {/* Advanced filters (collapsible) */}
                    <div className={`${showAdvancedFilters ? 'block' : 'hidden'} mt-2 min-w-0`}>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 min-w-0">
                            <div className="min-w-0">
                                <label className="block text-xs font-medium text-slate-700 mb-1.5">Owner</label>
                                <ReactSelect
                                    placeholder="All"
                                    classNamePrefix="react-select"
                                    isClearable
                                    options={[{ value: '', label: 'All' }, ...owners]}
                                    value={filters.owner_code ? (owners.find(o => o.value === filters.owner_code) || null) : null}
                                    onChange={(opt) => setFilters({ ...filters, owner_code: opt?.value || '' })}
                                />
                            </div>
                            <div className="min-w-0">
                                <label className="block text-xs font-medium text-slate-700 mb-1.5">Car (Plate No)</label>
                                <Input
                                    placeholder="e.g. KDK 123A"
                                    value={filters.plate_no}
                                    onChange={(e) => setFilters({ ...filters, plate_no: e.target.value.trim() })}
                                    className="text-sm min-w-0 max-w-full"
                                    disabled={!!plateNo}
                                    title={plateNo ? 'Filtered by car page' : 'Filter by plate number'}
                                />
                            </div>
                            <div className="min-w-0">
                                <label className="block text-xs font-medium text-slate-700 mb-1.5">Invoiced</label>
                                <ReactSelect
                                    placeholder="All"
                                    classNamePrefix="react-select"
                                    isClearable
                                    options={[
                                        { value: '', label: 'All' },
                                        { value: 'true', label: 'Invoiced' },
                                        { value: 'false', label: 'Uninvoiced' }
                                    ]}
                                    value={filters.invoiced !== '' ? { value: filters.invoiced, label: filters.invoiced === 'true' ? 'Invoiced' : 'Uninvoiced' } : null}
                                    onChange={(opt) => setFilters({ ...filters, invoiced: opt?.value || '' })}
                                />
                            </div>
                            <div className="min-w-0">
                                <label className="block text-xs font-medium text-slate-700 mb-1.5">Period</label>
                                <ReactSelect
                                    placeholder="Any period"
                                    classNamePrefix="react-select"
                                    isClearable
                                    options={[
                                        { value: 'this_week',   label: 'This week' },
                                        { value: 'last_week',   label: 'Last week' },
                                        { value: 'this_month',  label: 'This month' },
                                        { value: 'last_month',  label: 'Last month' },
                                    ]}
                                    value={activePeriod ? { value: activePeriod, label: { this_week: 'This week', last_week: 'Last week', this_month: 'This month', last_month: 'Last month' }[activePeriod] } : null}
                                    onChange={(opt) => applyPeriod(opt?.value || null)}
                                />
                            </div>
                            <div className="min-w-0">
                                <label className="block text-xs font-medium text-slate-700 mb-1.5">Status</label>
                                <ReactSelect
                                    placeholder="All"
                                    classNamePrefix="react-select"
                                    isClearable
                                    options={[
                                        { value: '', label: 'All' },
                                        ...stages.map(stage => ({ value: stage.code, label: stage.name }))
                                    ]}
                                    value={filters.status ? stages.find(s => s.code === filters.status) ? { value: filters.status, label: stages.find(s => s.code === filters.status)?.name } : null : null}
                                    onChange={(opt) => setFilters({ ...filters, status: opt?.value || '' })}
                                />
                            </div>
                            <div className="min-w-0">
                                <label className="block text-xs font-medium text-slate-700 mb-1.5">Location</label>
                                <ReactSelect
                                    placeholder="All"
                                    classNamePrefix="react-select"
                                    isClearable
                                    options={[
                                        { value: '', label: 'All' },
                                        { value: 'in_house', label: 'In-House' },
                                        { value: 'external', label: 'External' }
                                    ]}
                                    value={filters.service_location ? { value: filters.service_location, label: filters.service_location === 'in_house' ? 'In-House' : 'External' } : null}
                                    onChange={(opt) => setFilters({ ...filters, service_location: opt?.value || '' })}
                                />
                            </div>
                            <div className="min-w-0">
                                <label className="block text-xs font-medium text-slate-700 mb-1.5">From Date</label>
                                <Input
                                    type="date"
                                    value={filters.date_from}
                                    onChange={(e) => { setActivePeriod(null); setFilters({ ...filters, date_from: e.target.value }); }}
                                    className="text-sm w-full"
                                />
                            </div>
                            <div className="min-w-0">
                                <label className="block text-xs font-medium text-slate-700 mb-1.5">To Date</label>
                                <Input
                                    type="date"
                                    value={filters.date_to}
                                    onChange={(e) => { setActivePeriod(null); setFilters({ ...filters, date_to: e.target.value }); }}
                                    className="text-sm w-full"
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Stats Bar */}
            <div className="px-4 sm:px-6 lg:px-8 py-2">
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                    <div className="bg-white rounded-lg border border-slate-200 shadow-sm px-4 py-3 flex items-center gap-3">
                        <div className="w-9 h-9 rounded-lg bg-slate-100 flex items-center justify-center flex-shrink-0">
                            <FontAwesomeIcon icon={faWrench} className="text-slate-500 text-sm" />
                        </div>
                        <div>
                            <p className="text-xs text-slate-500 font-medium">Total</p>
                            <p className="text-xl font-bold text-slate-900 leading-tight">{stats.total}</p>
                        </div>
                    </div>
                    <div className="bg-white rounded-lg border border-amber-200 shadow-sm px-4 py-3 flex items-center gap-3">
                        <div className="w-9 h-9 rounded-lg bg-amber-50 flex items-center justify-center flex-shrink-0">
                            <FontAwesomeIcon icon={faLockOpen} className="text-amber-500 text-sm" />
                        </div>
                        <div>
                            <p className="text-xs text-amber-600 font-medium">Open</p>
                            <p className="text-xl font-bold text-amber-700 leading-tight">{stats.open}</p>
                        </div>
                    </div>
                    <div className="bg-white rounded-lg border border-blue-200 shadow-sm px-4 py-3 flex items-center gap-3">
                        <div className="w-9 h-9 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0">
                            <FontAwesomeIcon icon={faSyncAlt} className="text-blue-500 text-sm" />
                        </div>
                        <div>
                            <p className="text-xs text-blue-600 font-medium">In Progress</p>
                            <p className="text-xl font-bold text-blue-700 leading-tight">{stats.inProgress}</p>
                        </div>
                    </div>
                    <div className="bg-white rounded-lg border border-orange-200 shadow-sm px-4 py-3 flex items-center gap-3">
                        <div className="w-9 h-9 rounded-lg bg-orange-50 flex items-center justify-center flex-shrink-0">
                            <FontAwesomeIcon icon={faChartBar} className="text-[#FF5733] text-sm" />
                        </div>
                        <div>
                            <p className="text-xs text-[#FF5733] font-medium">Total Cost</p>
                            <p className="text-lg font-bold text-[#FF5733] leading-tight">{formatCurrency(stats.totalCost)}</p>
                        </div>
                    </div>
                </div>
            </div>

            {selectedCardIds.size > 0 && (
                <div className="px-4 sm:px-6 lg:px-8 pb-2">
                    <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-3 sm:p-4">
                        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
                            <div>
                                <Typography variant="small" className="text-slate-900 font-semibold">
                                    {selectedCardIds.size} job card{selectedCardIds.size !== 1 ? 's' : ''} selected
                                </Typography>
                                <Typography variant="small" className="text-slate-600">
                                    Apply one status change to all selected rows on this page.
                                </Typography>
                            </div>
                            <div className="flex flex-col sm:flex-row sm:items-center gap-2 w-full lg:w-auto">
                                <div className="min-w-[220px]">
                                    <ReactSelect
                                        placeholder="Choose status"
                                        classNamePrefix="react-select"
                                        isClearable
                                        isSearchable={false}
                                        options={statusOptions}
                                        value={bulkStatus ? statusOptions.find(opt => opt.value === bulkStatus) || null : null}
                                        onChange={(opt) => setBulkStatus(opt?.value || '')}
                                    />
                                    <div className="mt-1 text-xs text-slate-500">
                                        {stagesLoading
                                            ? 'Loading status choices...'
                                            : stagesError
                                                ? 'Using fallback status choices.'
                                                : 'Choose the target status for all selected rows.'}
                                    </div>
                                </div>
                                <Button
                                    className="bg-[#FF5733] hover:bg-[#e64520] text-white"
                                    onClick={handleOpenBulkConfirm}
                                    disabled={!bulkStatus || bulkLoading}
                                >
                                    {bulkLoading ? 'Updating...' : 'Apply Status'}
                                </Button>
                                <button
                                    type="button"
                                    className="px-4 py-2 rounded-sm border border-slate-300 text-slate-700 text-sm font-medium hover:bg-slate-50 transition-colors"
                                    onClick={clearBulkSelection}
                                    disabled={bulkLoading}
                                >
                                    Clear selection
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Content Section */}
            <div className="px-4 sm:px-6 lg:px-8">
                {loading ? (
                    // Skeleton Loading State
                    <div className="space-y-3">
                        {[1, 2, 3, 4].map((i) => (
                            <div key={i} className="bg-white rounded-lg border border-slate-200 p-4 sm:p-5 animate-pulse">
                                <div className="flex flex-col sm:flex-row sm:justify-between gap-3">
                                    <div className="flex-1 space-y-2">
                                        <div className="h-4 bg-slate-200 rounded w-32"></div>
                                        <div className="h-3 bg-slate-100 rounded w-48"></div>
                                    </div>
                                    <div className="h-6 bg-slate-200 rounded w-20"></div>
                                </div>
                            </div>
                        ))}
                    </div>
                ) : jobCards.length === 0 ? (
                    // Empty State
                    <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-8 sm:p-12 text-center">
                        <div className="inline-flex items-center justify-center w-16 h-16 bg-slate-100 rounded-full mb-4">
                            <FontAwesomeIcon icon={faExclamationTriangle} className="text-slate-400 text-2xl" />
                        </div>
                        <Typography variant="h6" className="text-slate-900 font-semibold mb-2">
                            No Job Cards Found
                        </Typography>
                        <Typography variant="small" className="text-slate-600 mb-6">
                            {searchTerm || Object.values(filters).some(v => v)
                                ? 'Try adjusting your filters or search terms'
                                : 'Create a new job card to get started'}
                        </Typography>
                        {onCreate && (
                            <Button
                                size="sm"
                                className="bg-[#FF5733] hover:bg-[#ff6b47] text-white inline-flex items-center gap-2"
                                onClick={onCreate}
                            >
                                <FontAwesomeIcon icon={faPlus} />
                                Create Job Card
                            </Button>
                        )}
                    </div>
                ) : (
                    <>
                        {/* Table + Mobile list + Pagination wrapped for consistent alignment */}
                        <div className="bg-white rounded-lg border border-slate-200 shadow-sm overflow-hidden">
                            {/* Desktop Table View */}
                            <div className="hidden lg:block overflow-x-auto">
                                    <table className="w-full text-sm table-fixed min-w-[980px]">
                                    <thead className="bg-slate-50 border-b border-slate-200">
                                        <tr>
                                            <th className="px-3 py-3 text-center font-semibold text-slate-700 w-[48px] min-w-[48px]">
                                                <input
                                                    type="checkbox"
                                                    checked={isAllVisibleSelected}
                                                    onChange={toggleSelectAllVisible}
                                                    aria-label="Select all job cards on this page"
                                                    onClick={(e) => e.stopPropagation()}
                                                />
                                            </th>
                                            <th className="px-5 py-3 text-left font-semibold text-slate-700 w-[140px] min-w-[120px]">Job Card No</th>
                                            <th className="px-5 py-3 text-left font-semibold text-slate-700 w-[130px] min-w-[120px]">Plate No</th>
                                            <th className="px-5 py-3 text-left font-semibold text-slate-700 w-[100px] min-w-[80px]">Owner</th>
                                            <th className="px-5 py-3 text-left font-semibold text-slate-700 w-[95px] min-w-[85px]">Date</th>
                                            <th className="px-5 py-3 text-left font-semibold text-slate-700 w-[95px] min-w-[85px]">Location</th>
                                            <th className="px-5 py-3 text-left font-semibold text-slate-700 w-[200px] min-w-[180px]">Status</th>
                                            <th className="px-5 py-3 text-right font-semibold text-slate-700 w-[100px] min-w-[90px]">Total Cost</th>
                                            <th className="px-5 py-3 text-center font-semibold text-slate-700 w-[100px] min-w-[90px]">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-200">
                                        {jobCards.map((card) => (
                                            <tr
                                                key={card.id}
                                                className="hover:bg-slate-50 transition-colors cursor-pointer"
                                                onClick={(e) => {
                                                    if (e.target.closest('.no-row-click')) return;
                                                    onView && onView(card);
                                                }}
                                            >
                                                <td className="px-3 py-4 align-middle text-center no-row-click">
                                                    <input
                                                        type="checkbox"
                                                        checked={selectedCardIds.has(card.id)}
                                                        onChange={() => toggleCardSelection(card)}
                                                        aria-label={`Select job card ${card.job_card_no}`}
                                                        disabled={selectedStatus && selectedStatus !== card.status}
                                                        onClick={(e) => e.stopPropagation()}
                                                    />
                                                </td>
                                                <td className="px-5 py-4 align-middle overflow-visible">
                                                    <div className="inline-flex items-center gap-1.5 max-w-full">
                                                        <span className="font-semibold text-slate-900 text-sm truncate">{card.job_card_no}</span>
                                                        {card.diagnosis && (
                                                            <FontAwesomeIcon
                                                                icon={faCircleInfo}
                                                                className="text-slate-400 hover:text-slate-600 text-xs flex-shrink-0 cursor-help transition-colors"
                                                                onMouseEnter={(e) => {
                                                                    const r = e.currentTarget.getBoundingClientRect();
                                                                    setDiagnosisTooltip({ visible: true, x: r.left - 12, y: r.bottom + 8, text: card.diagnosis });
                                                                }}
                                                                onMouseLeave={() => setDiagnosisTooltip({ visible: false, x: 0, y: 0, text: '' })}
                                                            />
                                                        )}
                                                    </div>
                                                </td>
                                                <td className="px-5 py-4 align-middle overflow-hidden">
                                                    <Typography
                                                        variant="small"
                                                        className="text-blue-600 hover:text-blue-800 cursor-pointer font-medium underline whitespace-nowrap block"
                                                        onClick={() => navigate(`/cars/${card.plate_no}`)}
                                                        title={card.plate_no}
                                                    >
                                                        {card.plate_no}
                                                    </Typography>
                                                </td>
                                                <td className="px-5 py-4 align-middle overflow-hidden">
                                                    {card.owner_name && card.owner_code ? (
                                                        <Typography
                                                            variant="small"
                                                            className="text-blue-600 hover:text-blue-800 cursor-pointer font-medium underline truncate block"
                                                            onClick={() => navigate(`/owners/statements/${card.owner_code}`)}
                                                            title={card.owner_name}
                                                        >
                                                            {card.owner_name}
                                                        </Typography>
                                                    ) : (
                                                        <Typography variant="small" className="text-slate-700">
                                                            —
                                                        </Typography>
                                                    )}
                                                </td>
                                                <td className="px-5 py-4 align-middle">
                                                    <Typography variant="small" className="text-slate-700 whitespace-nowrap" title={formatDate(card.opened_date)}>
                                                        {formatDate(card.opened_date)}
                                                    </Typography>
                                                </td>
                                                <td className="px-5 py-4 align-middle">
                                                    <Chip
                                                        value={card.service_location === 'in_house' ? 'In-House' : 'External'}
                                                        color={getServiceLocationColor(card.service_location)}
                                                        size="sm"
                                                        className="w-fit"
                                                    />
                                                </td>
                                                <td className="px-5 py-4 align-middle no-row-click">
                                                    <span
                                                        className="inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold"
                                                        style={getStatusStyle(card.status)}
                                                    >
                                                        {getStatusMeta(card.status).label}
                                                    </span>
                                                </td>
                                                <td className="px-5 py-4 text-right align-middle whitespace-nowrap">
                                                    <Typography variant="small" className="font-semibold text-slate-900" title={formatCurrency(card.total_cost)}>
                                                        {formatCurrency(card.total_cost)}
                                                    </Typography>
                                                </td>
                                                <td className="px-5 py-4 align-middle no-row-click">
                                                    <div className="flex justify-center no-row-click">
                                                        <button
                                                            className="flex h-7 w-7 items-center justify-center rounded-full border border-slate-300 text-slate-500 hover:bg-slate-100 hover:border-slate-400 transition-colors no-row-click"
                                                            onClick={(e) => openActionMenu(e, card.id, card)}
                                                        >
                                                            <FontAwesomeIcon icon={faEllipsisV} className="text-xs" />
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            {/* Mobile/Tablet Card View (kept inside same wrapper for consistent width on desktop) */}
                            <div className="lg:hidden px-4 py-3 space-y-3">
                                {jobCards.map((card) => (
                                    <div
                                    key={card.id}
                                    className="bg-white rounded-lg border border-slate-200 shadow-sm hover:shadow-md transition-shadow overflow-hidden cursor-pointer"
                                    onClick={(e) => { if (e.target.closest('.no-row-click')) return; onView && onView(card); }}
                                >
                                        {/* Card Header */}
                                        <div className="bg-gradient-to-r from-slate-50 to-slate-100 border-b border-slate-200 px-4 py-3 flex justify-between items-start gap-3">
                                            <div className="flex items-start gap-3 flex-1 min-w-0">
                                                <div className="pt-1 no-row-click">
                                                    <input
                                                        type="checkbox"
                                                        checked={selectedCardIds.has(card.id)}
                                                        onChange={() => toggleCardSelection(card)}
                                                        aria-label={`Select job card ${card.job_card_no}`}
                                                        disabled={selectedStatus && selectedStatus !== card.status}
                                                        onClick={(e) => e.stopPropagation()}
                                                    />
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                <div className="inline-flex items-center gap-1.5 max-w-full">
                                                    <span className="text-slate-900 font-bold truncate text-base">{card.job_card_no}</span>
                                                    {card.diagnosis && (
                                                        <FontAwesomeIcon
                                                            icon={faCircleInfo}
                                                            className="text-slate-400 hover:text-slate-600 text-xs flex-shrink-0 cursor-help transition-colors"
                                                            onMouseEnter={(e) => {
                                                                const r = e.currentTarget.getBoundingClientRect();
                                                                setDiagnosisTooltip({ visible: true, x: r.left, y: r.top - 8, text: card.diagnosis });
                                                            }}
                                                            onMouseLeave={() => setDiagnosisTooltip({ visible: false, x: 0, y: 0, text: '' })}
                                                        />
                                                    )}
                                                </div>
                                                <Typography variant="small" className="text-slate-600 mt-0.5">
                                                    <span
                                                        className="text-blue-600 hover:text-blue-800 cursor-pointer font-medium underline"
                                                        onClick={(e) => { e.stopPropagation(); navigate(`/cars/${card.plate_no}`); }}
                                                    >
                                                        {card.plate_no}
                                                    </span>
                                                </Typography>
                                                </div>
                                            </div>
                                            <div className="flex-shrink-0 no-row-click">
                                                <span
                                                    className="inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold"
                                                    style={getStatusStyle(card.status)}
                                                >
                                                    {getStatusMeta(card.status).label}
                                                </span>
                                            </div>
                                        </div>

                                        {/* Card Body */}
                                        <div className="px-4 py-4 space-y-3">
                                            {/* Details Grid */}
                                            <div className="grid grid-cols-2 gap-4">
                                                <div>
                                                    <Typography variant="small" className="text-slate-600 font-medium text-xs uppercase tracking-wide">
                                                        Owner
                                                    </Typography>
                                                    {card.owner_name && card.owner_code ? (
                                                        <Typography
                                                            variant="small"
                                                            className="text-blue-600 hover:text-blue-800 cursor-pointer font-medium mt-1 underline"
                                                            onClick={() => navigate(`/owners/statements/${card.owner_code}`)}
                                                        >
                                                            {card.owner_name}
                                                        </Typography>
                                                    ) : (
                                                        <Typography variant="small" className="text-slate-900 font-medium mt-1">
                                                            —
                                                        </Typography>
                                                    )}
                                                </div>
                                                <div>
                                                    <Typography variant="small" className="text-slate-600 font-medium text-xs uppercase tracking-wide">
                                                        Date
                                                    </Typography>
                                                    <Typography variant="small" className="text-slate-900 font-medium mt-1">
                                                        {formatDate(card.opened_date)}
                                                    </Typography>
                                                </div>
                                                <div>
                                                    <Typography variant="small" className="text-slate-600 font-medium text-xs uppercase tracking-wide">
                                                        Location
                                                    </Typography>
                                                    <div className="mt-1">
                                                        <Chip
                                                            value={card.service_location === 'in_house' ? 'In-House' : 'External'}
                                                            color={getServiceLocationColor(card.service_location)}
                                                            size="sm"
                                                            className="w-fit"
                                                        />
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Cost Highlight */}
                                            <div className="bg-gradient-to-br from-[#FF5733]/10 to-[#FF5733]/5 rounded-lg p-3 border border-[#FF5733]/20">
                                                <Typography variant="small" className="text-[#FF5733] font-medium text-xs uppercase tracking-wide">
                                                    Total Cost
                                                </Typography>
                                                <Typography variant="h6" className="text-[#FF5733] font-bold mt-1">
                                                    {formatCurrency(card.total_cost)}
                                                </Typography>
                                            </div>
                                        </div>

                                        {/* Card Footer - Actions */}
                                        <div className="border-t border-slate-200 bg-slate-50 px-4 py-3 flex justify-end no-row-click">
                                            <button
                                                className="flex h-7 w-7 items-center justify-center rounded-full border border-slate-300 text-slate-500 hover:bg-slate-100 hover:border-slate-400 transition-colors no-row-click"
                                                onClick={(e) => openActionMenu(e, `m-${card.id}`, card)}
                                            >
                                                <FontAwesomeIcon icon={faEllipsisV} className="text-xs" />
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>

                            {/* Pagination Controls - moved inside same white wrapper for correct alignment */}
                            <div className="border-t border-slate-200 px-4 py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                                <div className="text-sm text-slate-700">
                                    Showing {startItem <= (totalCount || jobCards.length) ? startItem : 0}-{endItem} of {totalCount || jobCards.length}
                                </div>
                                <div className="flex items-center gap-2 justify-end">
                                    <label className="text-xs text-slate-600">Per page</label>
                                    <select
                                        value={pageSize}
                                        onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }}
                                        className="text-sm px-2 py-1 rounded border bg-white"
                                    >
                                        <option value={10}>10</option>
                                        <option value={20}>20</option>
                                        <option value={50}>50</option>
                                        <option value={100}>100</option>
                                    </select>
                                    <button
                                        onClick={() => setPage(p => Math.max(1, p - 1))}
                                        disabled={page <= 1}
                                        className="px-3 py-1 bg-slate-100 rounded disabled:opacity-50"
                                    >
                                        Prev
                                    </button>
                                    <div className="text-sm text-slate-700">{page} / {pages}</div>
                                    <button
                                        onClick={() => setPage(p => Math.min(pages, p + 1))}
                                        disabled={page >= pages}
                                        className="px-3 py-1 bg-slate-100 rounded disabled:opacity-50"
                                    >
                                        Next
                                    </button>
                                </div>
                            </div>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default JobCardsList;
